import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format a EUR price. Pass `locale` (e.g. "en-US" or "fr-FR") to control
 * thousand separators / decimal style. Defaults to fr-FR for back-compat
 * with the original callsites; the i18n-aware components pass the active
 * locale explicitly.
 */
export type ReportingCurrency = "EUR" | "USD" | "CHF";

export function formatEur(
  value: number | null | undefined,
  opts: { compact?: boolean; locale?: string } = {},
): string {
  return formatPrice(value, { ...opts, currency: "EUR" });
}

export function formatPrice(
  value: number | null | undefined,
  opts: { compact?: boolean; locale?: string; currency?: ReportingCurrency } = {},
): string {
  if (value == null) return "—";
  const locale = opts.locale ?? "fr-FR";
  const currency = opts.currency ?? "EUR";
  if (opts.compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatKm(value: number | null | undefined, locale = "fr-FR"): string {
  if (value == null) return "—";
  return `${new Intl.NumberFormat(locale).format(value)} km`;
}

export function formatNumber(value: number | null | undefined, locale = "fr-FR"): string {
  if (value == null) return "—";
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Relative time string. Uses Intl.RelativeTimeFormat for proper
 * pluralization in both locales — "1 hour ago" vs "il y a 2 h".
 */
export function formatRelative(
  date: Date | string | null | undefined,
  locale = "fr-FR",
): string {
  if (date == null) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  if (diffSec < 60) return locale.startsWith("fr") ? "à l'instant" : "just now";
  if (diffSec < 3600) return rtf.format(-Math.floor(diffSec / 60), "minute");
  if (diffSec < 86400) return rtf.format(-Math.floor(diffSec / 3600), "hour");
  if (diffSec < 30 * 86400) return rtf.format(-Math.floor(diffSec / 86400), "day");
  return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
}

export type DealRating = "super" | "good" | "fair" | "high" | "over" | "unknown";

// Deal labels are intentionally NOT translated: "Super Price", "Good",
// "Fair" etc. are product nomenclature (like "Gold/Silver"), readable
// in both languages.
export const DEAL_LABELS: Record<DealRating, string> = {
  super: "Super Price",
  good: "Good",
  fair: "Fair",
  high: "High",
  over: "Over",
  unknown: "—",
};

export const DEAL_ORDER: DealRating[] = ["super", "good", "fair", "high", "over", "unknown"];
